export type DraftPayload = {
  schemaVersion: 1;
  scenarios: unknown[];
  sites: unknown[];
  activeId: string | null;
};

export type DoltIdentity = {
  issuer: string;
  userId: string;
  workspaceId: string;
};
export type PendingDraft = {
  baseRevision: number;
  requestId: string;
  payload: DraftPayload;
  state: "pending" | "conflict";
};
export type LoadResult = {
  revision: number;
  payload: DraftPayload | null;
  pending: PendingDraft | null;
};
export type SaveAck = {
  revision: number;
  payloadHash: string;
  commitId: string | null;
  idempotent: boolean;
};
export type RevisionResult = {
  revision: number;
  payload: DraftPayload;
  [key: string]: unknown;
};
export type PendingReconciliation =
  | { status: "none" }
  | { status: "acknowledged"; revision: number; pending: PendingDraft }
  | { status: "conflict"; revision: number; pending: PendingDraft };

export class DoltClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly currentRevision?: number;

  constructor(status: number, code: string, currentRevision?: number) {
    super(code);
    this.name = "DoltClientError";
    this.status = status;
    this.code = code;
    this.currentRevision = currentRevision;
  }
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type TokenProvider = () => string | null | Promise<string | null>;
type FetchLike = typeof fetch;

export type DoltClientOptions = {
  fetchImpl: FetchLike;
  storage: StorageLike;
  getToken: TokenProvider;
  identity: DoltIdentity;
  tabId: string;
  uuid?: () => string;
  /** Request deadline (token wait + fetch + response body), default 20s. */
  deadlineMs?: number;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HASH_RE = /^[0-9a-f]{64}$/i;
const API_BASE = "/api/data";
const REQUEST_DEADLINE_MS = 20_000;

function clientError(
  status: number,
  code: string,
  currentRevision?: number,
): DoltClientError {
  return new DoltClientError(status, code, currentRevision);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function validRevision(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function asPayload(value: unknown): DraftPayload | null {
  if (
    !isRecord(value) ||
    value.schemaVersion !== 1 ||
    !Array.isArray(value.scenarios) ||
    !Array.isArray(value.sites)
  )
    return null;
  if (value.activeId !== null && typeof value.activeId !== "string")
    return null;
  return value as DraftPayload;
}

function validateIdentity(identity: DoltIdentity, tabId: string): DoltIdentity {
  if (
    !UUID_RE.test(identity.userId) ||
    !UUID_RE.test(identity.workspaceId) ||
    !UUID_RE.test(tabId)
  ) {
    throw clientError(400, "invalid_identity");
  }
  try {
    const issuer = new URL(identity.issuer);
    if (
      issuer.protocol !== "https:" ||
      issuer.username ||
      issuer.password ||
      issuer.search ||
      issuer.hash ||
      issuer.toString() !== identity.issuer
    ) {
      throw new Error("invalid issuer");
    }
  } catch {
    throw clientError(400, "invalid_identity");
  }
  return identity;
}

function storageKey(identity: DoltIdentity, tabId: string): string {
  return `tenure.dolt.pending.v1:${encodeURIComponent(identity.issuer)}:${encodeURIComponent(identity.userId)}:${encodeURIComponent(identity.workspaceId)}:${encodeURIComponent(tabId)}`;
}

function decodeToken(token: string): { sub?: unknown; iss?: unknown } | null {
  const encoded = token.split(".")[1];
  if (!encoded) return null;
  try {
    const padded = encoded
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(encoded.length / 4) * 4, "=");
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0),
    );
    return JSON.parse(new TextDecoder().decode(bytes)) as {
      sub?: unknown;
      iss?: unknown;
    };
  } catch {
    return null;
  }
}

type JsonSlot = "root" | "object-value" | "array-item";

/**
 * Rejects values JSON.stringify would silently corrupt: nonfinite numbers,
 * BigInt, symbols, functions, cycles, sparse arrays/undefined array entries,
 * and non-plain objects. Undefined object properties are allowed (omitted).
 */
function validateJsonValue(
  value: unknown,
  slot: JsonSlot,
  seen: Set<object>,
): void {
  if (value === undefined) {
    if (slot !== "object-value")
      throw new Error(`json slot ${slot}: undefined`);
    return;
  }
  if (value === null || typeof value === "string" || typeof value === "boolean")
    return;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("json: nonfinite number");
    return;
  }
  if (
    typeof value === "bigint" ||
    typeof value === "symbol" ||
    typeof value === "function"
  ) {
    throw new Error(`json: ${typeof value} not serializable`);
  }
  if (typeof value !== "object") throw new Error("json: unknown value");
  if (seen.has(value)) throw new Error("json: cyclic reference");
  seen.add(value);
  try {
    if (Array.isArray(value)) {
      for (let index = 0; index < value.length; index += 1) {
        if (!Object.prototype.hasOwnProperty.call(value, index))
          throw new Error("json: sparse array hole");
        validateJsonValue(value[index], "array-item", seen);
      }
      return;
    }
    if (
      Object.getPrototypeOf(value) !== Object.prototype &&
      Object.getPrototypeOf(value) !== null
    ) {
      throw new Error("json: non-plain object");
    }
    if (Object.getOwnPropertySymbols(value).length > 0) {
      throw new Error("json: symbol property not serializable");
    }
    for (const key of Object.keys(value)) {
      validateJsonValue(
        (value as Record<string, unknown>)[key],
        "object-value",
        seen,
      );
    }
  } finally {
    seen.delete(value);
  }
}

function frozenPayload(payload: DraftPayload): DraftPayload {
  try {
    validateJsonValue(payload, "root", new Set());
    const json = JSON.stringify(payload);
    if (typeof json !== "string") throw new Error("json did not serialize");
    const snapshot = asPayload(JSON.parse(json));
    if (!snapshot) throw new Error("invalid draft shape");
    return snapshot;
  } catch {
    throw clientError(400, "invalid_payload");
  }
}

function samePayload(left: DraftPayload, right: DraftPayload): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function pendingFromStorage(
  storage: StorageLike,
  key: string,
): PendingDraft | null {
  const raw = storage.getItem(key);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw clientError(500, "corrupt_pending_storage");
  }
  if (!isRecord(parsed)) throw clientError(500, "corrupt_pending_storage");
  const payload = asPayload(parsed.payload);
  if (
    !validRevision(parsed.baseRevision) ||
    typeof parsed.requestId !== "string" ||
    !UUID_RE.test(parsed.requestId) ||
    !payload
  ) {
    throw clientError(500, "corrupt_pending_storage");
  }
  if (parsed.state !== "pending" && parsed.state !== "conflict")
    throw clientError(500, "corrupt_pending_storage");
  return {
    baseRevision: parsed.baseRevision,
    requestId: parsed.requestId,
    payload,
    state: parsed.state,
  };
}

function responseError(status: number, body: unknown): DoltClientError {
  const record = isRecord(body) ? body : {};
  const code = typeof record.code === "string" ? record.code : "request_failed";
  const currentRevision = validRevision(record.currentRevision)
    ? record.currentRevision
    : undefined;
  return clientError(status, code, currentRevision);
}

export function createDoltClient(options: DoltClientOptions) {
  const identity = validateIdentity(options.identity, options.tabId);
  const key = storageKey(identity, options.tabId);
  // `window.fetch` requires its Window receiver in some browsers. Calling it
  // through `options` binds the options object instead, so capture the callable
  // before the request lifecycle invokes it.
  const fetchImpl = options.fetchImpl;
  const controller = new AbortController();
  const uuid = options.uuid ?? (() => crypto.randomUUID());
  let disposed = false;
  let loadedRevision: number | null = null;
  let loadedPayload: DraftPayload | null = null;
  let queueTail = Promise.resolve();
  let queued = 0;

  const assertActive = () => {
    if (disposed) throw clientError(499, "disposed");
  };

  const enqueue = <T>(work: () => Promise<T>): Promise<T> => {
    if (queued >= 8) return Promise.reject(clientError(503, "save_busy"));
    queued += 1;
    const previous = queueTail;
    let release!: () => void;
    queueTail = new Promise<void>((resolve) => {
      release = resolve;
    });
    return (async () => {
      await previous;
      queued -= 1;
      try {
        assertActive();
        return await work();
      } finally {
        release();
      }
    })();
  };

  const pending = (): PendingDraft | null =>
    pendingFromStorage(options.storage, key);

  const writePending = (record: PendingDraft) => {
    assertActive();
    options.storage.setItem(key, JSON.stringify(record));
  };

  const deadlineMs =
    Number.isFinite(options.deadlineMs) && (options.deadlineMs as number) > 0
      ? (options.deadlineMs as number)
      : REQUEST_DEADLINE_MS;

  const request = async (
    path: string,
    init: RequestInit = {},
  ): Promise<unknown> => {
    assertActive();
    const requestController = new AbortController();
    let timedOut = false;
    const abortForDisposal = () => requestController.abort();
    controller.signal.addEventListener("abort", abortForDisposal, {
      once: true,
    });
    const timer = setTimeout(() => {
      timedOut = true;
      requestController.abort();
    }, deadlineMs);
    const abortError = () =>
      disposed
        ? clientError(499, "disposed")
        : timedOut
          ? clientError(503, "request_timeout")
          : clientError(503, "request_aborted");
    try {
      return await rawRequest(path, init, requestController.signal, abortError);
    } finally {
      clearTimeout(timer);
      controller.signal.removeEventListener("abort", abortForDisposal);
    }
  };

  const waitFor = <T>(
    operation: Promise<T>,
    signal: AbortSignal,
    abortError: () => DoltClientError,
  ): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      if (signal.aborted) {
        reject(abortError());
        return;
      }
      const onAbort = () => {
        signal.removeEventListener("abort", onAbort);
        reject(abortError());
      };
      signal.addEventListener("abort", onAbort, { once: true });
      operation.then(
        (value) => {
          signal.removeEventListener("abort", onAbort);
          resolve(value);
        },
        (error: unknown) => {
          signal.removeEventListener("abort", onAbort);
          reject(error);
        },
      );
    });

  const rawRequest = async (
    path: string,
    init: RequestInit,
    signal: AbortSignal,
    abortError: () => DoltClientError,
  ): Promise<unknown> => {
    assertActive();
    const token = await waitFor(
      Promise.resolve().then(() => options.getToken()),
      signal,
      abortError,
    );
    if (signal.aborted) throw abortError();
    assertActive();
    if (typeof token !== "string") throw clientError(401, "unauthorized");
    const claims = decodeToken(token);
    if (
      !claims ||
      claims.sub !== identity.userId ||
      claims.iss !== identity.issuer
    )
      throw clientError(401, "identity_mismatch");
    if (!path.startsWith(`${API_BASE}/`))
      throw clientError(400, "invalid_api_path");
    let response: Response;
    try {
      // Gate dispatch after token resolution: an expired request must never
      // turn a late token into a write.
      if (signal.aborted) throw abortError();
      response = await waitFor(
        Promise.resolve().then(() =>
          fetchImpl(path, {
            ...init,
            headers: { Authorization: `Bearer ${token}`, ...init.headers },
            signal,
          }),
        ),
        signal,
        abortError,
      );
    } catch (error) {
      if (error instanceof DoltClientError) throw error;
      if (signal.aborted) throw abortError();
      throw clientError(503, "network_error");
    }
    if (signal.aborted) throw abortError();
    assertActive();
    let body: unknown;
    try {
      body = await waitFor(
        Promise.resolve().then(() => response.json()),
        signal,
        abortError,
      );
    } catch (error) {
      if (error instanceof DoltClientError) throw error;
      if (signal.aborted) throw abortError();
      throw clientError(response.status || 503, "invalid_response");
    }
    // Do not accept a body which completed after the deadline/disposal.
    if (signal.aborted) throw abortError();
    assertActive();
    if (!response.ok) throw responseError(response.status, body);
    return body;
  };

  const draftPath = `${API_BASE}/v1/workspaces/${identity.workspaceId}/draft`;

  return {
    async load(): Promise<LoadResult> {
      return enqueue(async () => {
        const body = await request(draftPath);
        if (
          !isRecord(body) ||
          !validRevision(body.revision) ||
          (body.payload !== null && !asPayload(body.payload))
        ) {
          throw clientError(503, "invalid_response");
        }
        assertActive();
        loadedRevision = body.revision;
        loadedPayload = body.payload === null ? null : asPayload(body.payload)!;
        return {
          revision: body.revision,
          payload: loadedPayload,
          pending: pending(),
        };
      });
    },

    async save(payload: DraftPayload): Promise<SaveAck> {
      const snapshot = frozenPayload(payload);
      return enqueue(async () => {
        if (loadedRevision === null) throw clientError(409, "load_required");
        const existing = pending();
        if (existing && !samePayload(existing.payload, snapshot))
          throw clientError(409, "pending_review_required");
        let record = existing;
        if (!record) {
          const requestId = uuid();
          if (!UUID_RE.test(requestId))
            throw clientError(500, "invalid_request_id");
          record = {
            baseRevision: loadedRevision,
            requestId,
            payload: snapshot,
            state: "pending",
          };
          writePending(record);
        }
        try {
          const body = await request(draftPath, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              expectedRevision: record.baseRevision,
              requestId: record.requestId,
              payload: record.payload,
            }),
          });
          if (
            !isRecord(body) ||
            !validRevision(body.revision) ||
            typeof body.payloadHash !== "string" ||
            (body.commitId !== null && typeof body.commitId !== "string") ||
            typeof body.idempotent !== "boolean"
          ) {
            throw clientError(503, "invalid_response");
          }
          if (!HASH_RE.test(body.payloadHash))
            throw clientError(503, "invalid_response");
          if (
            body.revision !== record.baseRevision + 1 ||
            (loadedRevision !== record.baseRevision &&
              loadedRevision !== record.baseRevision + 1)
          ) {
            throw clientError(409, "ack_mismatch");
          }
          const current = pending();
          if (
            !current ||
            current.requestId !== record.requestId ||
            !samePayload(current.payload, record.payload)
          )
            throw clientError(409, "ack_mismatch");
          assertActive();
          options.storage.removeItem(key);
          loadedRevision = body.revision;
          return {
            revision: body.revision,
            payloadHash: body.payloadHash,
            commitId: body.commitId as string | null,
            idempotent: body.idempotent,
          };
        } catch (error) {
          if (error instanceof DoltClientError && error.status === 409) {
            const current = pending();
            if (current && current.requestId === record.requestId) {
              const remote = error.currentRevision;
              if (remote === undefined || remote > record.baseRevision + 1) {
                // Remote moved on: retain pending for deliberate reconciliation,
                // do not roll back the loaded revision or auto-clear anything.
                throw clientError(409, "reload_required", remote);
              }
              writePending({ ...current, state: "conflict" });
            }
          }
          throw error;
        }
      });
    },

    async history(): Promise<unknown[]> {
      const body = await request(
        `${API_BASE}/v1/workspaces/${identity.workspaceId}/history`,
      );
      if (!Array.isArray(body)) throw clientError(503, "invalid_response");
      return body;
    },

    async getRevision(revision: number): Promise<RevisionResult> {
      if (!validRevision(revision) || revision === 0)
        throw clientError(400, "invalid_revision");
      const body = await request(
        `${API_BASE}/v1/workspaces/${identity.workspaceId}/revisions/${revision}`,
      );
      if (
        !isRecord(body) ||
        body.revision !== revision ||
        !asPayload(body.payload)
      )
        throw clientError(503, "invalid_response");
      return body as RevisionResult;
    },

    pending,

    async reconcilePending(): Promise<PendingReconciliation> {
      return enqueue(async () => {
        const current = pending();
        if (!current) return { status: "none" };
        if (loadedRevision === null) throw clientError(409, "load_required");
        if (
          loadedRevision === current.baseRevision + 1 &&
          loadedPayload &&
          samePayload(loadedPayload, current.payload)
        ) {
          options.storage.removeItem(key);
          return {
            status: "acknowledged",
            revision: loadedRevision,
            pending: current,
          };
        }
        writePending({ ...current, state: "conflict" });
        return {
          status: "conflict",
          revision: loadedRevision,
          pending: current,
        };
      });
    },

    async discardPending(): Promise<PendingDraft | null> {
      return enqueue(async () => {
        const current = pending();
        if (!current) return null;
        // Return the exact persisted local copy so the caller can retain or
        // export it before it deliberately permits a new save.
        options.storage.removeItem(key);
        return current;
      });
    },

    dispose(): void {
      disposed = true;
      controller.abort();
    },
  };
}
